import "./_ProductAdd.scss";
import "./_ProductAddMobile.scss";
import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CustomDatePicker from "./CustomDatePicker";
import { FaTrash, FaTrashAlt } from "react-icons/fa";

function _ProductAdd({ setActivePanel }) {
  // Helper to reorder arrays
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newFiles = reorder(
      selectedFiles,
      result.source.index,
      result.destination.index
    );
    const newPreviews = reorder(
      productImages,
      result.source.index,
      result.destination.index
    );
    setSelectedFiles(newFiles);
    setProductImages(newPreviews);
  };
  const [categoryFocused, setCategoryFocused] = useState(false);
  const [manufacturerFocused, setManufacturerFocused] = useState(false);
  const [animeSeriesFocused, setAnimeSeriesFocused] = useState(false);

  // Form states
  const [categoryVal, setCategoryVal] = useState("");
  const [productNameVal, setProductNameVal] = useState("");
  const [manufacturerVal, setManufacturerVal] = useState("");
  const [animeSeriesVal, setAnimeSeriesVal] = useState("");
  const [productTypeVal, setProductTypeVal] = useState("ON HAND");
  const [origPriceVal, setOrigPriceVal] = useState("");
  const [salePriceVal, setSalePriceVal] = useState("");
  const [stockVal, setStockVal] = useState("");
  const [japanEstDate, setJapanEstDate] = useState("");
  const [preOrderDeadlineVal, setpreOrderDeadlineVal] = useState("");
  const [downPaymentVal, setDownPaymentVal] = useState("");

  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [animeSeries, setAnimeSeries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // File input and drag/drop
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [productImages, setProductImages] = useState([]);

  const handleSelectImages = () => fileInputRef.current.click();
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    appendFiles(files);
    e.target.value = null;
  };

  const appendFiles = (files) => {
    const allowedExt = ["jpg", "jpeg", "png", "webp", "heic", "heif"];

    const filtered = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      return allowedExt.includes(ext);
    });

    const newFiles = [...selectedFiles, ...filtered].slice(0, 6);

    const newPreviews = newFiles.map((file) => {
      const ext = file.name.split(".").pop().toLowerCase();

      // HEIC / HEIF cannot be previewed in most browsers
      if (["heic", "heif"].includes(ext)) {
        return "/images/image-placeholder.png"; // fallback icon
      }

      return URL.createObjectURL(file);
    });

    setSelectedFiles(newFiles);
    setProductImages(newPreviews);
  };

  const handleRemoveImage = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setProductImages(productImages.filter((_, i) => i !== index));
  };

  const handleRemoveAll = () => {
    setSelectedFiles([]);
    setProductImages([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    appendFiles(files);
  };

  const handleDragOver = (e) => e.preventDefault();

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/add-get-foreign`
        );
        const data = await res.json();
        setCategories(data.categories);
        setManufacturers(data.manufacturers);
        setAnimeSeries(data.animeSeries);
      } catch (err) {
        console.error("Failed to fetch dropdown data", err);
      }
    };
    fetchDropdowns();
  }, []);

  // Form validation
  const validateForm = () => {
    let newErrors = {};
    if (!categoryVal) newErrors.categoryVal = "Category is required";
    if (!productNameVal) newErrors.productNameVal = "Product Name is required";
    if (!manufacturerVal)
      newErrors.manufacturerVal = "Manufacturer is required";
    if (!animeSeriesVal) newErrors.animeSeriesVal = "Anime Series is required";
    if (!origPriceVal) newErrors.origPriceVal = "Original Price is required";

    if (productTypeVal === "ON HAND") {
      if (!salePriceVal) newErrors.salePriceVal = "Sales Price is required";
      if (!stockVal) newErrors.stockVal = "Stock is required";
    } else {
      if (!preOrderDeadlineVal)
        newErrors.preOrderDeadlineVal = "Pre-order Deadline is required";
      if (!japanEstDate)
        newErrors.japanEstDate = "Japan Release Date is required";
    }

    if (!downPaymentVal) newErrors.downPaymentVal = "Downpayment is required";
    if (!selectedFiles.length)
      newErrors.productImages = "At least 1 image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const categoryObj = categories.find((c) => c.PC_Name === categoryVal);
      const manufacturerObj = manufacturers.find(
        (m) => m.PM_Name === manufacturerVal
      );
      const animeObj = animeSeries.find((a) => a.AS_Name === animeSeriesVal);

      if (!selectedFiles || selectedFiles.length === 0) {
        alert("Please select at least one image.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("PC_ID", categoryObj?.PC_ID || "");
      formData.append("PD_Name", productNameVal);
      formData.append("PM_ID", manufacturerObj?.PM_ID || "");
      formData.append("AS_ID", animeObj?.AS_ID || "");
      formData.append("PD_ProductType", productTypeVal);
      formData.append("PD_OriginalPrice", origPriceVal);
      formData.append("PD_SalePrice", salePriceVal || "");
      formData.append("PD_Stock", stockVal || "");
      formData.append(
        "PD_JapanEst",
        japanEstDate ? new Date(japanEstDate).toISOString().slice(0, 10) : ""
      );
      formData.append(
        "PD_PreOrderDeadline",
        preOrderDeadlineVal
          ? new Date(preOrderDeadlineVal).toISOString().slice(0, 10)
          : ""
      );
      formData.append("PD_DownPayment", downPaymentVal || "");

      selectedFiles.forEach((file) =>
        formData.append("PD_ProductPicPath", file)
      );

      console.log("FormData ready:");
      for (let pair of formData.entries()) console.log(pair[0], pair[1]);

      const res = await fetch(
        `https://littlekidinside.shop/api/product/add-product`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await res.json();

      if (res.ok) {
        alert("Product saved successfully!");
        handleCancel();
      } else {
        alert(data.error || "Failed to save product.");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving product.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCategoryVal("");
    setProductNameVal("");
    setManufacturerVal("");
    setAnimeSeriesVal("");
    setProductTypeVal("ON HAND");
    setOrigPriceVal("");
    setSalePriceVal("");
    setStockVal("");
    setJapanEstDate("");
    setpreOrderDeadlineVal("");
    setDownPaymentVal("");
    setProductImages([]);
    setSelectedFiles([]);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setActivePanel(0);
  };

  const [highlightedCategoryIndex, setHighlightedCategoryIndex] = useState(-1);
  const [highlightedManufacturerIndex, setHighlightedManufacturerIndex] =
    useState(-1);
  const [highlightedAnimeIndex, setHighlightedAnimeIndex] = useState(-1);

  const categoryListRef = useRef(null);
  const manufacturerListRef = useRef(null);
  const animeListRef = useRef(null);
  const scrollIntoViewIfNeeded = (containerRef, index) => {
    if (!containerRef.current) return;
    const items = containerRef.current.querySelectorAll(".keyword-result");
    const focused = items[index];
    if (focused) {
      focused.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };
  useEffect(
    () => scrollIntoViewIfNeeded(categoryListRef, highlightedCategoryIndex),
    [highlightedCategoryIndex]
  );
  useEffect(
    () =>
      scrollIntoViewIfNeeded(manufacturerListRef, highlightedManufacturerIndex),
    [highlightedManufacturerIndex]
  );
  useEffect(
    () => scrollIntoViewIfNeeded(animeListRef, highlightedAnimeIndex),
    [highlightedAnimeIndex]
  );
  const handleKeyDownCategory = (e, filteredList) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedCategoryIndex((prev) =>
        Math.min(prev + 1, filteredList.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedCategoryIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedCategoryIndex >= 0) {
      e.preventDefault();
      setCategoryVal(filteredList[highlightedCategoryIndex].PC_Name);
      setCategoryFocused(false);
    }
  };
  const filteredCategories = categories.filter((c) =>
    c.PC_Name.toLowerCase().includes(categoryVal.toLowerCase())
  );
  const filteredManufacturers = manufacturers.filter((m) =>
    m.PM_Name.toLowerCase().includes(manufacturerVal.toLowerCase())
  );
  const filteredAnimeSeries = animeSeries.filter((a) =>
    a.AS_Name.toLowerCase().includes(animeSeriesVal.toLowerCase())
  );
  const handleKeyDownManufacturer = (e, filteredList) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedManufacturerIndex((prev) =>
        Math.min(prev + 1, filteredList.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedManufacturerIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedManufacturerIndex >= 0) {
      e.preventDefault();
      setManufacturerVal(filteredList[highlightedManufacturerIndex].PM_Name);
      setManufacturerFocused(false);
    }
  };

  const handleKeyDownAnime = (e, filteredList) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedAnimeIndex((prev) =>
        Math.min(prev + 1, filteredList.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedAnimeIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedAnimeIndex >= 0) {
      e.preventDefault();
      setAnimeSeriesVal(filteredList[highlightedAnimeIndex].AS_Name);
      setAnimeSeriesFocused(false);
    }
  };

  return (
    <div className="ap-container">
      {/* First row: left & right columns */}
      <div className="ap-first-row">
        <div className="ap-first-col">
          {/* Product Name */}
          <div className="ap-input-wrapper">
            <label className="ap-input-label">Product Name:</label>
            <textarea
              className="ap-input"
              value={productNameVal}
              onChange={(e) => setProductNameVal(e.target.value)}
            />
            {errors.productNameVal && (
              <span className="ap-error">{errors.productNameVal}</span>
            )}
          </div>
          {/* Category */}
          <div className="ap-input-wrapper">
            <label className="ap-input-label">Category:</label>
            <input
              type="text"
              value={categoryVal}
              onChange={(e) => {
                setCategoryVal(e.target.value);
                setErrors((prev) => ({ ...prev, categoryVal: "" }));
                setHighlightedCategoryIndex(-1);
              }}
              onFocus={() => setCategoryFocused(true)}
              onBlur={() => setTimeout(() => setCategoryFocused(false), 250)}
              onKeyDown={(e) => handleKeyDownCategory(e, filteredCategories)}
              style={{ borderColor: errors.categoryVal ? "#da4f4f" : "black" }}
              placeholder="Type or select category..."
            />

            {errors.categoryVal && (
              <span className="ap-error">{errors.categoryVal}</span>
            )}

            {categoryFocused && (
              <div className="ap-keyword-result" ref={categoryListRef}>
                {filteredCategories.map((item, index) => (
                  <div
                    key={item.PC_ID}
                    className={`keyword-result ${
                      index === highlightedCategoryIndex ? "highlighted" : ""
                    }`}
                    onMouseDown={() => {
                      setCategoryVal(item.PC_Name);
                      setCategoryFocused(false);
                    }}
                  >
                    {item.PC_Name}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Manufacturer */}
          <div className="ap-input-wrapper">
            <label className="ap-input-label">Manufacturer:</label>
            <input
              type="text"
              value={manufacturerVal}
              onChange={(e) => {
                setManufacturerVal(e.target.value);
                setErrors((prev) => ({ ...prev, manufacturerVal: "" }));
                setHighlightedManufacturerIndex(-1);
              }}
              onFocus={() => setManufacturerFocused(true)}
              onBlur={() =>
                setTimeout(() => setManufacturerFocused(false), 250)
              }
              onKeyDown={(e) =>
                handleKeyDownManufacturer(e, filteredManufacturers)
              }
              style={{
                borderColor: errors.manufacturerVal ? "#da4f4f" : "black",
              }}
              placeholder="Type or select manufacturer..."
            />

            {errors.manufacturerVal && (
              <span className="ap-error">{errors.manufacturerVal}</span>
            )}

            {manufacturerFocused && (
              <div className="ap-keyword-result" ref={manufacturerListRef}>
                {filteredManufacturers.map((item, index) => (
                  <div
                    key={item.PM_ID}
                    className={`keyword-result ${
                      index === highlightedManufacturerIndex
                        ? "highlighted"
                        : ""
                    }`}
                    onMouseDown={() => {
                      setManufacturerVal(item.PM_Name);
                      setManufacturerFocused(false);
                    }}
                  >
                    {item.PM_Name}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Anime Series */}
          <div className="ap-input-wrapper">
            <label className="ap-input-label">Anime Series:</label>
            <input
              type="text"
              value={animeSeriesVal}
              onChange={(e) => {
                setAnimeSeriesVal(e.target.value);
                setErrors((prev) => ({ ...prev, animeSeriesVal: "" }));
                setHighlightedAnimeIndex(-1);
              }}
              onFocus={() => setAnimeSeriesFocused(true)}
              onBlur={() => setTimeout(() => setAnimeSeriesFocused(false), 250)}
              onKeyDown={(e) => handleKeyDownAnime(e, filteredAnimeSeries)}
              style={{
                borderColor: errors.animeSeriesVal ? "#da4f4f" : "black",
              }}
              placeholder="Type or select anime series..."
            />

            {errors.animeSeriesVal && (
              <span className="ap-error">{errors.animeSeriesVal}</span>
            )}

            {animeSeriesFocused && (
              <div className="ap-keyword-result" ref={animeListRef}>
                {filteredAnimeSeries.map((item, index) => (
                  <div
                    key={item.AS_ID}
                    className={`keyword-result ${
                      index === highlightedAnimeIndex ? "highlighted" : ""
                    }`}
                    onMouseDown={() => {
                      setAnimeSeriesVal(item.AS_Name);
                      setAnimeSeriesFocused(false);
                    }}
                  >
                    {item.AS_Name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="ap-first-col">
          <div className="ap-input-wrapper">
            <label className="ap-input-label">Product Type:</label>
            <select
              value={productTypeVal}
              onChange={(e) => setProductTypeVal(e.target.value)}
              className="ap-select"
            >
              <option value="ON HAND">ON HAND</option>
              <option value="PRE-ORDER">PREORDER</option>
            </select>
            {errors.productTypeVal && (
              <span className="ap-error">{errors.productTypeVal}</span>
            )}
          </div>

          {/* Original Price */}

          {/* Conditional fields */}
          {productTypeVal === "ON HAND" ? (
            <>
              <div className="ap-input-wrapper">
                <label className="ap-input-label">Original Price:</label>
                <input
                  type="text"
                  value={origPriceVal}
                  onChange={(e) => {
                    const onlyDigits = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 11);
                    setOrigPriceVal(onlyDigits);
                  }}
                />
                {errors.origPriceVal && (
                  <span className="ap-error">{errors.origPriceVal}</span>
                )}
              </div>
              <div className="ap-input-wrapper">
                <label className="ap-input-label">Sales Price:</label>
                <input
                  type="text"
                  value={salePriceVal}
                  onChange={(e) => {
                    const onlyDigits = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 11);
                    setSalePriceVal(onlyDigits);
                  }}
                />
                {errors.salePriceVal && (
                  <span className="ap-error">{errors.salePriceVal}</span>
                )}
              </div>

              <div className="ap-input-wrapper">
                <label className="ap-input-label">Number of Stock:</label>
                <input
                  type="text"
                  value={stockVal}
                  onChange={(e) => {
                    const onlyDigits = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 11);
                    setStockVal(onlyDigits);
                  }}
                />
                {errors.stockVal && (
                  <span className="ap-error">{errors.stockVal}</span>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="ap-input-wrapper">
                <label className="ap-input-label">Pre Order Price:</label>
                <input
                  type="text"
                  value={origPriceVal}
                  onChange={(e) => {
                    const onlyDigits = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 11);
                    setOrigPriceVal(onlyDigits);
                  }}
                />
                {errors.origPriceVal && (
                  <span className="ap-error">{errors.origPriceVal}</span>
                )}
              </div>
              <div className="ap-input-wrapper">
                <label className="ap-input-label">Pre-Order Deadline:</label>
                <CustomDatePicker
                  date={preOrderDeadlineVal}
                  setDate={setpreOrderDeadlineVal}
                />
                {errors.preOrderDeadlineVal && (
                  <span className="ap-error">{errors.preOrderDeadlineVal}</span>
                )}
              </div>

              <div className="ap-input-wrapper">
                <label className="ap-input-label">
                  Japan Estimated Release Date:
                </label>
                <input
                  type="text"
                  value={japanEstDate}
                  onChange={(e) => setJapanEstDate(e.target.value)}
                />
                {errors.japanEstDate && (
                  <span className="ap-error">{errors.japanEstDate}</span>
                )}
              </div>
            </>
          )}

          {/* Downpayment */}
          <div className="ap-input-wrapper">
            <label className="ap-input-label">Downpayment:</label>
            <input
              type="text"
              value={downPaymentVal}
              onChange={(e) => {
                const onlyDigits = e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 11);
                setDownPaymentVal(onlyDigits);
              }}
            />
            {errors.downPaymentVal && (
              <span className="ap-error">{errors.downPaymentVal}</span>
            )}
          </div>
        </div>

        {/* Drag & Drop + Preview */}
        <div
          className="ap-image-col"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {productImages.length > 0 && (
            <span className="ap-remove-all" onClick={handleRemoveAll}>
              <FaTrashAlt />
            </span>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="images-droppable" direction="vertical">
              {(provided) => (
                <div
                  className="ap-image-col-wrapper"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {productImages.length > 0 ? (
                    productImages.map((src, index) => (
                      <Draggable
                        key={index.toString()}
                        draggableId={index.toString()}
                        index={index}
                      >
                        {(dragProvided) => (
                          <div
                            className="ap-image-item-wrapper"
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                          >
                            <div className="ap-image-wrapper">
                              <img src={src} alt={`Preview ${index}`} />
                            </div>
                            <div className="ap-img-file-name">
                              {selectedFiles[index]?.name}
                            </div>
                            <span
                              className="ap-trash-icon"
                              onClick={() => handleRemoveImage(index)}
                            >
                              <FaTrash />
                            </span>
                          </div>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <div className="ap-image-prev">
                      Drag & Drop Images Here or Click "UPLOAD IMAGES"
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="ap-input-wrapper">
            <button onClick={handleSelectImages}>UPLOAD IMAGES</button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleFileChange}
            />
            <p>FILE EXTENSION: .JPG, .JPEG, .PNG (max 6 images)</p>
            {errors.productImages && (
              <span className="ap-error">{errors.productImages}</span>
            )}
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="ap-btn-action-wrapper">
        <button className="cancel-btn" onClick={handleCancel}>
          Cancel
        </button>
        <button className="save-btn" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default _ProductAdd;
